import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { v2, resolveApiUrl } from '../../api/client';
import { VesselProfile, RiskBand, RISK_BG, RISK_COLORS } from '../../types/vessel';
import { operatorApi } from '../../lib/api/operator';
import ReadinessPill from './ReadinessPill';

/** 22.5°-sector compass point for a bearing ("SW", "NNE"). */
function compass(deg: number | null | undefined): string {
  if (deg == null) return '—';
  const pts = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return pts[Math.round(((deg % 360) + 360) % 360 / 22.5) % 16];
}

const SRP_STYLE: Record<string, { chip: string; label: string }> = {
  HRS:             { chip: 'bg-red-900/50 border-red-600 text-red-200',         label: 'High Risk Ship (est.)' },
  SRS:             { chip: 'bg-amber-900/40 border-amber-700 text-amber-200',   label: 'Standard Risk Ship (est.)' },
  'LRS-candidate': { chip: 'bg-emerald-900/40 border-emerald-700 text-emerald-200', label: 'Low Risk candidate (est.)' },
};

/** Estimated Paris MoU ship risk profile — the classification PSC regimes
 *  use to set inspection frequency. Labelled an estimate: RO and company
 *  performance aren't in our data; the official figure lives in THETIS. */
function SrpBlock({ vesselId }: { vesselId: string }) {
  const { data: srp } = useQuery({
    queryKey: ['vessel-srp', vesselId],
    queryFn: () => v2.getVesselSRP(vesselId),
    staleTime: 60 * 60_000,
    retry: 0,
  });
  if (!srp) return null;
  const style = SRP_STYLE[srp.profile] ?? SRP_STYLE.SRS;

  return (
    <div className="mt-5 rounded-lg bg-gray-800/60 border border-gray-700 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-gray-300 text-xs uppercase tracking-wider">Paris MoU ship risk profile</h3>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${style.chip}`}>
          {style.label} · {srp.points} pts
        </span>
      </div>
      {srp.factors.length > 0 && (
        <div className="mt-2 space-y-1">
          {srp.factors.map((f) => (
            <div key={f.id} className="flex justify-between text-xs py-1 border-b border-gray-700/60">
              <span className="text-gray-400">{f.label}</span>
              <span className="text-gray-200 font-mono">+{f.points}</span>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
        Estimated from held data ({srp.unknowns.join('; ')}).{' '}
        <a href={srp.official_calculator} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
          Official THETIS calculator →
        </a>
      </p>
    </div>
  );
}

/** Official PSC record sources, deep-linkable only to their search pages. */
function PscSourceLinks({ imo }: { imo?: string | null }) {
  const links = [
    { name: 'THETIS inspections (Paris MoU)', url: 'https://portal.emsa.europa.eu/web/thetis/inspections' },
    { name: 'THETIS current detentions', url: 'https://portal.emsa.europa.eu/web/thetis/current-detentions' },
    { name: 'Paris MoU inspection search', url: 'https://parismou.org/inspection-search/inspection-search' },
    { name: 'Tokyo MOU (APCIS)', url: 'https://apcis.tmou.org/public/' },
    { name: 'Equasis (free account)', url: 'https://www.equasis.org' },
  ];
  return (
    <div className="mt-4 rounded-lg bg-gray-800/60 border border-gray-700 p-3">
      <h3 className="text-gray-300 text-xs uppercase tracking-wider mb-1.5">Official PSC records</h3>
      {imo && (
        <p className="text-[11px] text-gray-500 mb-1.5">
          Search by IMO <span className="font-mono text-gray-300">{imo}</span> on:
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {links.map((l) => (
          <a
            key={l.name}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] px-2 py-1 rounded border border-gray-600 text-blue-300 hover:text-blue-200 hover:border-blue-500/60"
          >
            {l.name} ↗
          </a>
        ))}
      </div>
    </div>
  );
}

/** Live wind/sea/visibility at the vessel's AIS position (Open-Meteo). */
function ConditionsBlock({ lat, lng }: { lat: number; lng: number }) {
  const { data: wx } = useQuery({
    queryKey: ['weather', lat.toFixed(2), lng.toFixed(2)],
    queryFn: () => v2.getWeather(lat, lng),
    staleTime: 15 * 60_000,
    retry: 1,
  });
  if (!wx) return null;

  const items: Array<[string, string]> = [];
  if (wx.wind_speed_kn != null) {
    items.push(['Wind', `${Math.round(wx.wind_speed_kn)} kn ${compass(wx.wind_direction_deg)}${
      wx.wind_gusts_kn != null ? ` · gusts ${Math.round(wx.wind_gusts_kn)}` : ''}`]);
  }
  if (wx.wave_height_m != null) {
    items.push(['Waves', `${wx.wave_height_m} m ${compass(wx.wave_direction_deg)}${
      wx.wave_period_s != null ? ` · ${Math.round(wx.wave_period_s)} s` : ''}`]);
  }
  if (wx.swell_height_m != null) items.push(['Swell', `${wx.swell_height_m} m`]);
  if (wx.temperature_c != null) items.push(['Air temp', `${Math.round(wx.temperature_c)} °C`]);
  if (wx.visibility_m != null) items.push(['Visibility', wx.visibility_m >= 10_000 ? '≥10 km' : `${(wx.visibility_m / 1000).toFixed(1)} km`]);
  if (items.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg bg-sky-950/40 border border-sky-800/50 p-3">
      <div className="flex items-baseline justify-between mb-1.5">
        <h3 className="text-sky-300 text-xs uppercase tracking-wider">Conditions at position</h3>
        <span className="text-[10px] text-gray-500">open-meteo.com</span>
      </div>
      <div className="grid grid-cols-2 gap-x-4">
        {items.map(([label, value]) => (
          <div key={label} className="flex justify-between py-1 border-b border-sky-900/40 text-sm">
            <span className="text-gray-400">{label}</span>
            <span className="text-gray-100 font-medium">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface Props {
  vesselId: string;
  onClose: () => void;
}

type Tab = 'overview' | 'voyage' | 'psc' | 'risk' | 'owner';

function RiskBadge({ band, score }: { band: RiskBand; score: number }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${RISK_BG[band]}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: RISK_COLORS[band] }} />
      {band.toUpperCase()} — {score}/100
    </span>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3 p-4">
      <div className="h-6 bg-gray-700 rounded w-3/4" />
      <div className="h-4 bg-gray-700 rounded w-1/2" />
      <div className="h-4 bg-gray-700 rounded w-2/3" />
      <div className="h-24 bg-gray-700 rounded mt-4" />
      <div className="h-24 bg-gray-700 rounded" />
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-gray-700 text-sm">
      <span className="text-gray-400">{label}</span>
      <span className="text-gray-100 font-medium text-right max-w-[60%] break-words">{value ?? '—'}</span>
    </div>
  );
}

export default function VesselProfileDrawer({ vesselId, onClose }: Props) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');

  const { data: vessel, isLoading, error } = useQuery<VesselProfile>({
    queryKey: ['vessel', vesselId],
    queryFn: () => v2.getVessel(vesselId),
    staleTime: 30_000,
  });

  // Canonical server-side readiness — same number as profile/search/fleet.
  const { data: readiness } = useQuery({
    queryKey: ['vessel-readiness', vesselId],
    queryFn: () => operatorApi.getReadiness(vesselId),
    staleTime: 5 * 60_000,
    retry: 0,
  });

  const hasActiveSanctions = vessel?.sanctions?.some(s => !s.removal_date);

  // CC-licensed photo from Wikimedia Commons — partial coverage, so the
  // header simply omits the image when the lookup comes back empty.
  const { data: photo } = useQuery({
    queryKey: ['vessel-photo', vesselId],
    queryFn: () => v2.getVesselPhoto(vesselId),
    staleTime: 24 * 60 * 60_000,
    retry: 0,
  });

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'voyage',   label: 'Voyage' },
    { id: 'psc',      label: 'PSC' },
    { id: 'risk',     label: 'Risk' },
    { id: 'owner',    label: 'Owner' },
  ];

  return (
    <div className="fixed right-0 top-0 h-full w-[480px] max-w-full z-20 bg-gray-900 border-l border-gray-700 flex flex-col shadow-2xl">
      {/* ── Ship photo (Wikimedia Commons, CC — credit overlaid) ── */}
      {photo?.found && photo.thumb && (
        <div className="relative shrink-0 h-40 bg-gray-800 overflow-hidden">
          <img
            src={resolveApiUrl(photo.thumb)}
            alt={vessel?.name ?? 'Vessel photo'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-900/90 to-transparent px-3 pb-1.5 pt-6">
            <a
              href={photo.page_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-gray-300/90 hover:text-white"
              title="Photo source and licence on Wikimedia Commons"
            >
              📷 {photo.credit} · {photo.license} · Wikimedia Commons
            </a>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-700 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {isLoading ? (
              <div className="h-6 bg-gray-700 rounded w-48 animate-pulse" />
            ) : (
              <h2 className="text-white font-bold text-lg leading-tight truncate">
                {vessel?.name ?? vesselId}
              </h2>
            )}
            {vessel && (
              <p className="text-gray-400 text-xs mt-0.5">
                {vessel.imo ? `IMO: ${vessel.imo}` : ''}{vessel.imo && vessel.mmsi ? '  |  ' : ''}{vessel.mmsi ? `MMSI: ${vessel.mmsi}` : ''}{vessel.flag_iso2 ? `  |  Flag: ${vessel.flag_iso2}` : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate(`/vessels/${vessel?.id ?? vesselId}`)}
              className="text-blue-400 hover:text-blue-300 text-xs underline whitespace-nowrap"
            >
              Full Profile →
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
          </div>
        </div>

        {(vessel?.risk || readiness) && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {readiness && (
              <ReadinessPill score={readiness.readiness_score} band={readiness.readiness_band} />
            )}
            {vessel?.risk && (
              <RiskBadge band={vessel.risk.risk_band} score={vessel.risk.overall_score} />
            )}
          </div>
        )}

        {hasActiveSanctions && (
          <div className="mt-2 flex items-center gap-2 bg-red-900/40 border border-red-700 rounded px-3 py-1.5 text-xs text-red-300">
            <span>⚠</span>
            <span>Active sanctions — {vessel!.sanctions.filter(s => !s.removal_date).map(s => s.list_source).join(', ')}</span>
          </div>
        )}
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-gray-700 shrink-0 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap transition-colors ${
              tab === t.id
                ? 'border-b-2 border-blue-500 text-blue-400'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && <Skeleton />}
        {error && <p className="text-red-400 text-sm">{String(error)}</p>}

        {vessel && tab === 'overview' && (
          <div className="space-y-1">
            <Row label="Type"             value={vessel.vessel_type} />
            <Row label="Flag"             value={vessel.flag_iso2} />
            <Row label="Year Built"       value={vessel.year_built} />
            <Row label="Gross Tonnage"    value={vessel.gross_tonnage?.toLocaleString()} />
            <Row label="Deadweight"       value={vessel.deadweight?.toLocaleString()} />
            <Row label="Class Society"    value={vessel.class_society} />
            <Row label="Class Status"     value={vessel.class_status} />
            <Row label="Class Expiry"     value={vessel.class_expiry} />
            <Row label="Registered Owner" value={vessel.registered_owner} />
            <Row label="Ship Manager"     value={vessel.ship_manager} />
          </div>
        )}

        {vessel && tab === 'voyage' && (
          <div>
            {vessel.live ? (
              <div className="space-y-1">
                <Row label="Latitude"    value={vessel.live.lat?.toFixed(4)} />
                <Row label="Longitude"   value={vessel.live.lng?.toFixed(4)} />
                <Row label="Speed"       value={vessel.live.speed != null ? `${vessel.live.speed} kn` : undefined} />
                <Row label="Course"      value={vessel.live.course != null ? `${vessel.live.course}°` : undefined} />
                <Row label="Status"      value={vessel.live.status} />
                <Row label="Destination" value={vessel.live.destination} />
                <Row label="Last AIS"    value={vessel.live.ts} />
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No live voyage data available.</p>
            )}
            {vessel.live?.lat != null && vessel.live?.lng != null && (
              <ConditionsBlock lat={vessel.live.lat} lng={vessel.live.lng} />
            )}
            {vessel.recent_port_calls?.length > 0 && (
              <div className="mt-4">
                <h3 className="text-gray-300 text-xs uppercase tracking-wider mb-2">Recent Port Calls</h3>
                {vessel.recent_port_calls.slice(0, 5).map(pc => (
                  <div key={pc.id} className="text-sm py-1.5 border-b border-gray-700">
                    <span className="text-white">{pc.port_name ?? pc.port_locode ?? '—'}</span>
                    <span className="text-gray-400 text-xs ml-2">{pc.arrived_at ? new Date(pc.arrived_at).toLocaleDateString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {vessel && tab === 'psc' && (
          <div>
            {vessel.recent_inspections?.length === 0 ? (
              <>
                <p className="text-gray-400 text-sm">
                  No PSC records held for this vessel yet. VarunAIq's PSC data is
                  imported in batches and doesn't cover every ship — the official
                  MOU databases below are authoritative.
                </p>
                <PscSourceLinks imo={vessel.imo} />
              </>
            ) : (
              <>
                <div className="flex gap-4 mb-3 text-sm">
                  <span className="text-gray-400">Total: <span className="text-white font-medium">{vessel.recent_inspections?.length ?? 0}</span></span>
                  <span className="text-gray-400">Detentions: <span className="text-red-400 font-medium">{vessel.recent_inspections?.filter(i => i.detained).length ?? 0}</span></span>
                </div>
                <div className="space-y-2">
                  {vessel.recent_inspections?.slice(0, 5).map(insp => (
                    <div key={insp.id} className={`rounded p-2.5 text-sm border ${insp.detained ? 'border-red-700 bg-red-900/20' : 'border-gray-700 bg-gray-800'}`}>
                      <div className="flex justify-between">
                        <span className="text-white font-medium">{insp.port_name}</span>
                        <span className="text-gray-400 text-xs">{insp.inspection_date}</span>
                      </div>
                      <div className="flex gap-2 mt-1 text-xs">
                        <span className="text-gray-400">{insp.mou}</span>
                        {insp.detained && <span className="text-red-400 font-medium">DETAINED</span>}
                        {insp.deficiency_count > 0 && <span className="text-amber-400">{insp.deficiency_count} deficiencies</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <PscSourceLinks imo={vessel.imo} />
              </>
            )}
          </div>
        )}

        {vessel && tab === 'risk' && (
          <div>
            {vessel.risk ? (
              <>
                <div className="text-center mb-4">
                  <div className="text-5xl font-bold" style={{ color: RISK_COLORS[vessel.risk.risk_band] }}>
                    {vessel.risk.overall_score}
                  </div>
                  <div className="text-gray-300 text-sm mt-1">{vessel.risk.risk_band.toUpperCase()} RISK</div>
                </div>
                <div className="space-y-1 mb-4">
                  {(['identity','movement','compliance','ownership','condition'] as const).map(k => {
                    const score = vessel.risk![(k + '_score') as keyof typeof vessel.risk] as number | undefined;
                    return score != null ? (
                      <div key={k} className="flex justify-between text-sm py-1 border-b border-gray-700">
                        <span className="text-gray-400 capitalize">{k}</span>
                        <span className="text-gray-100">{score}</span>
                      </div>
                    ) : null;
                  })}
                </div>
                <h3 className="text-gray-300 text-xs uppercase tracking-wider mb-2">Risk Signals</h3>
                {vessel.risk.signals?.length === 0 ? (
                  <p className="text-gray-400 text-sm">No significant signals.</p>
                ) : (
                  vessel.risk.signals?.map((s, i) => (
                    <div key={i} className="text-sm py-1.5 border-b border-gray-700">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs mr-2 ${s.severity === 'critical' ? 'bg-red-900 text-red-300' : s.severity === 'high' ? 'bg-orange-900 text-orange-300' : s.severity === 'medium' ? 'bg-amber-900 text-amber-300' : 'bg-gray-700 text-gray-300'}`}>
                        {s.severity}
                      </span>
                      {s.description}
                    </div>
                  ))
                )}
              </>
            ) : (
              <p className="text-gray-400 text-sm">No risk score computed yet.</p>
            )}
            <SrpBlock vesselId={vesselId} />
          </div>
        )}

        {vessel && tab === 'owner' && (
          <div className="space-y-2">
            {vessel.ownership?.length === 0 ? (
              <p className="text-gray-400 text-sm">No ownership records found.</p>
            ) : (
              vessel.ownership?.map((o, i) => (
                <div key={i} className="rounded p-2.5 bg-gray-800 border border-gray-700 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-xs uppercase">{o.ownership_type.replace('_', ' ')}</span>
                    {o.valid_to && <span className="text-gray-500 text-xs">Until {o.valid_to}</span>}
                  </div>
                  <div className="text-white font-medium mt-0.5">{o.company_name}</div>
                </div>
              ))
            )}
            {hasActiveSanctions && (
              <div className="mt-3">
                <h3 className="text-gray-300 text-xs uppercase tracking-wider mb-2">Sanctions</h3>
                {vessel.sanctions.filter(s => !s.removal_date).map((s, i) => (
                  <div key={i} className="rounded p-2.5 bg-red-900/30 border border-red-700 text-sm mb-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs mr-2 ${s.list_source === 'OFAC' ? 'bg-red-800 text-red-200' : s.list_source === 'UK_HMT' ? 'bg-blue-800 text-blue-200' : 'bg-purple-800 text-purple-200'}`}>
                      {s.list_source}
                    </span>
                    <span className="text-red-200">{s.designation}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
